import React, { useMemo, useState } from 'react';
import { MenuItem, MenuSeparator } from '../components/Menu';
import { BEACON_TOPIC_SIDEBAR } from '../constants/onboarding';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import drillDownIcon from '../icons/non-prefilled/16x16/drillDown.svg';
import favoriteAddIcon from '../icons/non-prefilled/16x16/favoriteAdd.svg';
import favoriteRemoveIcon from '../icons/non-prefilled/16x16/favoriteRemove.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import topicIcon from '../icons/prefilled/16x16/topic.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import { getPostIcon, getPostState } from '../post/PostIcon';
import PostTitleWithIcons from '../post/PostTitleWithIcons';
import { useFavoritePost } from '../post/useFavoritePosts';
import TopicNameWithIcon from '../topic/TopicNameWithIcon';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import sortTopic from '../topic/utils/sortTopic';
import postPath, { createPostPath } from '../utils/url/postPath';
import topicPath from '../utils/url/topicPath';
import styles from './SidebarItems.module.scss';
import SidebarRow, { SidebarRowRightContent } from './SidebarRow';
import SidebarSectionAnimation from './SidebarSectionAnimation';
import TopicItemMenuItems from './TopicItemMenuItems';
import sortSidebarItems from './sortSidebarItems';
import useAppSelector from '../app/redux/useAppSelector';

const POST_FRAGMENT = gql(`
  fragment SidebarItems_post on SlimPostFields {
    id
    type
    ...PostTitleWithIcons_post
  }
`);

type PostFragment = DocumentType<typeof POST_FRAGMENT>;

interface FavoritePostMenuItemProps {
  postId: string;
}
const FavoritePostMenuItem = ({ postId }: FavoritePostMenuItemProps) => {
  const { isFavorite, toggleFavorite } = useFavoritePost(postId);

  return (
    <MenuItem
      key={isFavorite ? 'unfavorite' : 'favorite'}
      icon={isFavorite ? favoriteRemoveIcon : favoriteAddIcon}
      onClick={() => toggleFavorite()}
    >
      {isFavorite ? 'Unfavorite post' : 'Favorite post'}
    </MenuItem>
  );
};

interface SidebarItemsProps {
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
  posts?: PostFragment[];
  topics: AllTopicsTreeListTopic[];
  orderIds?: string[];
}

type RenderItem =
  | (AllTopicsTreeListTopic & { sidebarType: 'topic' })
  | (PostFragment & { sidebarType: 'post' });

const SidebarItems = ({
  activeTopics,
  expandedTopics,
  posts = [],
  topics,
  orderIds,
}: SidebarItemsProps) => {
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const [forcedExpandedKeys, setForceExpandedKeys] = useState<
    Record<string, boolean>
  >({});

  const items: RenderItem[] = useMemo(() => {
    const combined: RenderItem[] = [
      ...topics.map((topic) => ({ ...topic, sidebarType: 'topic' as const })),
      ...posts.map((post) => ({ ...post, sidebarType: 'post' as const })),
    ];
    return Array.isArray(orderIds)
      ? sortSidebarItems(combined, orderIds)
      : combined;
  }, [topics, posts, orderIds]);

  const renderTopic = (topic: AllTopicsTreeListTopic, path: string[]) => {
    const { childTopics, id } = topic;
    const key = [...path, id].join('.');
    const isForceExpanded = forcedExpandedKeys[key];
    const isExpanded =
      isForceExpanded !== undefined
        ? isForceExpanded
        : expandedTopics[id] || activeTopics[id];

    const isActive = activeTopics[id];

    const childPath = [...path, id];
    const childComponent =
      isExpanded && childTopics.length > 0 ? (
        <ul className={styles.list}>
          {childTopics
            .toSorted(sortTopic)
            .map((t) => renderTopic(t, childPath))}
        </ul>
      ) : null;

    return (
      <TutorialBeacon
        key={key}
        id={BEACON_TOPIC_SIDEBAR}
        hide={!isActive}
        placement="right"
      >
        {(beaconProps) => (
          <li {...beaconProps} aria-label={topic.name}>
            <SidebarRow
              subItem
              level={path.length}
              icon={path.length === 0 ? topicIcon : null}
              to={topicPath(topic)}
              type="topic"
              onClick={() => {
                if (selectedSectionId != null) {
                  setForceExpandedKeys({
                    ...forcedExpandedKeys,
                    [key]: !isExpanded,
                  });
                }
              }}
              rightContent={
                <SidebarRowRightContent
                  menuItems={<TopicItemMenuItems topic={topic} />}
                  expanded={isExpanded}
                  onExpandClick={
                    topic.childTopics.length > 0
                      ? () =>
                          setForceExpandedKeys({
                            ...forcedExpandedKeys,
                            [key]: !isExpanded,
                          })
                      : undefined
                  }
                />
              }
              active={isActive}
            >
              <TopicNameWithIcon
                topic={topic}
                iconSize="regular"
                truncate="multi"
              />
            </SidebarRow>
            <SidebarSectionAnimation sectionKey={id} expanded={!!isExpanded}>
              {childComponent}
            </SidebarSectionAnimation>
          </li>
        )}
      </TutorialBeacon>
    );
  };

  const renderPost = (post: PostFragment) => {
    const postState = getPostState(post);

    return (
      <li key={post.id}>
        <SidebarRow
          subItem
          icon={getPostIcon(post, !postState.draft)}
          to={postPath(post)}
          type={post.type === PostType.Template ? 'template' : 'post'}
          rightContent={
            <SidebarRowRightContent
              menuItems={
                post.type === PostType.Template ? (
                  <>
                    <MenuItem
                      icon={postIcon}
                      to={createPostPath({ templateId: post.id })}
                    >
                      Use template
                    </MenuItem>
                    <MenuSeparator />
                    <MenuItem icon={drillDownIcon} to={postPath(post)}>
                      View template
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <FavoritePostMenuItem postId={post.id} />
                    <MenuItem icon={drillDownIcon} to={postPath(post)}>
                      View post
                    </MenuItem>
                  </>
                )
              }
            />
          }
        >
          <PostTitleWithIcons
            post={post}
            truncate="multi"
            iconSize="small"
            showVerifiedIcon={false}
            publicIconMuted
          />
        </SidebarRow>
      </li>
    );
  };

  return (
    <ul className={styles.list}>
      {items.map((item) => {
        if (item.sidebarType === 'post') {
          return renderPost(item);
        }
        if (item.sidebarType === 'topic') {
          return renderTopic(item, []);
        }
        return null;
      })}
    </ul>
  );
};

export default SidebarItems;
